import classNames from 'classnames'
import { Link } from 'gatsby'
import React from 'react'
import Image from '@toasttab/shared/src/components/atoms/Image/Image'
import { GridCardProps } from './GridCard.d'

const GridCard = ({
  arrowHover,
  backgroundColor = 'transparent',
  children,
  classes,
  horizScale = '30/70',
  horizWrap = true,
  image,
  imageSize,
  layout = 'vertical',
  onClick,
  scaleImageHover = true,
  shadowHover = true,
  url,
  variation = null
}: GridCardProps) => {
  const containerWrapper = classNames(classes, {
    'group block': true,
    'transition-all duration-200 ease-in-out relative': true,
    'bg-white': backgroundColor === 'white',
    'max-w-screen-sm': layout === 'vertical',
    'hover:shadow-otl-default': shadowHover,
    // Base arrow styles
    'after:content-icon-right-arrow-orange after:absolute after:right-5 after:top-4 hover:after:left-[calc(100%-40px)] after:inline-block \
     after:opacity-0 hover:after:opacity-100 after:transition-all after:duration-300 after:left-3/4':
      layout === 'horizontal' && arrowHover,
    'relative after:content-icon-right-arrow-orange after:absolute after:right-5 after:top-4 hover:after:left-[calc(100%-40px)] after:inline-block \
    after:opacity-0 hover:after:opacity-100 after:transition-all after:duration-300 after:left-3/4':
      layout === 'vertical' && arrowHover
  })

  const linkWrapper = classNames({
    flex: layout === 'horizontal',
    'flex-wrap h-full': layout === 'horizontal' && horizWrap,
    'flex-row':
      layout === 'horizontal' &&
      (horizScale === '10/90' || horizScale === '20/80'),
    'flex-col md:flex-row':
      layout === 'horizontal' &&
      (horizScale === '30/70' || horizScale === '50/50')
  })

  const imageWrapper = classNames({
    'w-full h-full mx-auto transition-all duration-200 ease-out':
      layout === 'horizontal'
  })

  const colLeftWrapper = classNames({
    'pb-4': layout === 'vertical',
    'basis-1/6 px-2': layout === 'horizontal' && horizScale === '10/90',
    'basis-1/4 px-2': layout === 'horizontal' && horizScale === '20/80',
    'pl-0 pr-2':
      layout === 'horizontal' &&
      horizScale === '20/80' &&
      variation === 'otl-featured-article',
    'pl-0':
      layout === 'horizontal' &&
      horizScale === '20/80' &&
      variation === 'otl-related-articles-item',
    'basis-1/3 md:px-4': layout === 'horizontal' && horizScale === '30/70',
    'basis-2/4 pr-4': layout === 'horizontal' && horizScale === '50/50',
    'p-4':
      layout === 'horizontal' &&
      horizScale === '50/50' &&
      variation === 'otl-promo-block'
  })

  const colRightWrapper = classNames({
    'basis-5/6 px-2': layout === 'horizontal' && horizScale === '10/90',
    'basis-full md:basis-3/4 pl-2 pr-5':
      layout === 'horizontal' && horizScale === '20/80',
    'py-3':
      layout === 'horizontal' &&
      horizScale === '20/80' &&
      variation === 'otl-featured-article',
    'basis-2/3 md:px-4': layout === 'horizontal' && horizScale === '30/70',
    'basis-2/4 pl-4': layout === 'horizontal' && horizScale === '50/50',
    'basis-full h-full flex flex-col justify-between':
      layout === 'horizontal' && horizScale === '100',
    'p-4':
      layout === 'horizontal' &&
      horizScale === '50/50' &&
      variation === 'otl-promo-block'
  })

  const imageSizeCss = classNames({
    'h-auto w-auto': imageSize === 'auto',
    'h-[69px] w-[69px] min-w-[69px] lg:h-[96px] lg:w-[96px] lg:min-w-[96px] mx-auto':
      imageSize === 'xxs',
    'h-[188px] md:h-[114px]': imageSize === 'xs',
    'h-[188px]': imageSize === 'sm',
    'h-[250px]': imageSize === 'md',
    'h-[320px]': imageSize === 'lg',
    'h-[250px] md:h-[350px]': imageSize === 'xl',
    'h-full': imageSize === 'full'
  })

  return (
    <>
      {url && (
        <Link className={containerWrapper} data-testid='GridCard-link' to={url}>
          <div className={linkWrapper} data-testid='GridCard-link-wrapper'>
            {image && (
              <div
                className={colLeftWrapper}
                data-testid='GridCard-link-leftCol'
              >
                <div
                  className={imageWrapper}
                  data-testid='GridCard-link-imageWrapper'
                >
                  <Image
                    url={image.url}
                    title={image.title}
                    imageAlt={image.imageAlt}
                    optimizedImages={image.optimizedImages}
                    classes={imageSizeCss}
                    scaleHover={scaleImageHover}
                  />
                </div>
              </div>
            )}
            <div
              className={colRightWrapper}
              data-testid='GridCard-link-rightCol'
            >
              {children}
            </div>
          </div>
        </Link>
      )}
      {!url && (
        <div
          className={containerWrapper}
          data-testid='GridCard'
          onClick={onClick}
        >
          <div className={linkWrapper} data-testid='GridCard-wrapper'>
            {image && (
              <div className={colLeftWrapper} data-testid='GridCard-leftCol'>
                <div
                  className={imageWrapper}
                  data-testid='GridCard-imageWrapper'
                >
                  <Image
                    url={image.url}
                    title={image.title}
                    imageAlt={image.imageAlt}
                    optimizedImages={image.optimizedImages}
                    classes={imageSizeCss}
                    scaleHover={scaleImageHover}
                  />
                </div>
              </div>
            )}
            <div className={colRightWrapper} data-testid='GridCard-rightCol'>
              {children}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default GridCard
